/*
 * Copyright 2024 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import {
    AppLoadingIndicatorV1,
    useAuthProviderV1,
} from "@startree-ui/platform-ui";
import { AxiosError } from "axios";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { EnvironmentInfo } from "../../rest/environment-info/environment-info.interfaces";
import { getEnvironmentInfo } from "../../rest/environment-info/environment-info.rest";
import {
    EnvironmentFlags,
    getEnvironmentFlags,
} from "../../utils/environment/environment.utils";
import { checkIfNewQueryConsoleAvailable } from "../../utils/pinot/pinot.utils";
import { AppError } from "../app-error/app-error.component";

export interface EnvironmentInfoContext {
    /** Raw information returned by the API */
    envInfo: EnvironmentInfo;
    /**  Computed flags based on the raw information
     * added here to avoid the same logic everywhere */
    envFlags: EnvironmentFlags;
}

export const EnvironmentInfoProvider: FunctionComponent = ({ children }) => {
    const [envInfo, setEnvInfo] = useState<EnvironmentInfo>(
        {} as EnvironmentInfo
    );
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { t } = useTranslation();
    const { authenticated, authDisabled } = useAuthProviderV1();
    const authDone = authenticated || authDisabled;

    const [isStarTreeQCAvailable, setIsStarTreeQCAvailable] = useState(false);

    useEffect(() => {
        // if not authenticated continue loading the app and let the app handle logout redirect
        if (!authDone) {
            setIsLoading(false);

            return;
        }

        checkIfNewQueryConsoleAvailable().then((isAvailable) => {
            setIsStarTreeQCAvailable(isAvailable);
        });

        fetchEnvInfo();
    }, []);

    const fetchEnvInfo = async (): Promise<void> => {
        try {
            const envInfo = await getEnvironmentInfo();
            setEnvInfo(envInfo);
        } catch (error) {
            const axiosError = error as AxiosError;

            // if the error is not 401, then show the error message
            if (axiosError?.response?.status !== 401) {
                setIsError(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <AppLoadingIndicatorV1 />;
    }

    // if the envInfo api returns error show error and block the app
    if (!isLoading && isError) {
        return (
            <AppError
                details={t(
                    "message.please-try-reloading-the-page-or-contact-startree-support"
                )}
                header={t("message.error-occurred-fetching-environment-info")}
            />
        );
    }

    return (
        <EnvironmentInfoContext.Provider
            value={{
                // Raw information returned by the API
                envInfo,
                // Computed flags based on the raw information
                // added here to avoid the same logic everywhere
                envFlags: getEnvironmentFlags(envInfo, isStarTreeQCAvailable),
            }}
        >
            {children}
        </EnvironmentInfoContext.Provider>
    );
};

const EnvironmentInfoContext = createContext<EnvironmentInfoContext>(
    {} as EnvironmentInfoContext
);

export const useEnvironmentInfoContext = (): EnvironmentInfoContext => {
    return useContext(EnvironmentInfoContext);
};
